import React, { useState, useMemo } from "react";
import s from "./SendMessageWrapper.module.scss";
import ReusableModal from "../../../../../../global components/Modal/Modal";
import Card from "./local components/Card";
import ReactEmailEditor from "./local components/ReactEmailEditor";
import { BaselineCheckCircle } from "../../../../../../global components/Icons/BaseLineCheckCircle";
import { CloseIcon } from "../../../../../../global components/Icons/CloseIcon";
import { generateResult } from "../../../../../../global components/ResultModal/ResultModal";
import ReusableDrawer from "../../../../../../global components/Drawer/ReuseableDrawer";
import SetSchedule from "./local components/SetSchedule";
import { Badge, Button, Input } from "antd";
import clsx from "clsx";
import Sent from "./local components/Sent/Index";
import { UseAuthToken } from "../../../../../../libs/api/userSession";
import useSWR from "swr";
import {
  parseClientError,
  swrFetcher,
  swrMutationFetcher,
  SwrMutationFetcherArgType,
} from "../../../../../../libs/api/Endpoints";
import { PaginatedApplicationMessage } from "../../../../../../libs/Utils/Types/User";
import TabsTemplate from "../../../../../../global components/TabsTemplate/TabsTemplte";
import useSWRMutation from "swr/mutation";
import { SuccessDataType } from "../../../../../../libs/api/types";

function SendMessageWrapper() {
  const [openModal, setOpenModal] = useState(false);
  const [openEditor, setOpenEditor] = useState(false);
  const [contentJSONDraft, setContentJSONDraft] = useState<any>();
  const [contentHTMLDraft, setContentHTMLDraft] = useState<any>();

  const [openGroup, setOpenGroup] = useState(false);
  const [msgTitle, setMsgTitle] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [audience, setAudience] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageItems, setPageItems] = useState(50);
  const [filter, setFilter] = useState("");
  const [activeTabKey, setActiveTabKey] = useState("1");
  const [userType, setUserType] = useState<
    "verified" | "unverified" | "pending" | "all" | null
  >(null);

  const token = UseAuthToken();
  const { trigger: submitMessage, isMutating } = useSWRMutation(
    "api/admin/app-messages",
    swrMutationFetcher
  );
  const { trigger: submitDraftMessage, isMutating: isSubmitting } =
    useSWRMutation("api/admin/app-messages", swrMutationFetcher);
  // -------------------------------------------
  // 🔹 Consolidate shared query parameters
  // -------------------------------------------
  const query = useMemo(
    () => ({
      ...(searchValue && { search: searchValue }),
      ...(filter && { messageStatus: filter }),
      ...(pageItems && { limit: pageItems }),
      ...(currentPage && { page: currentPage }),
      ...(audience && { audience }),
      ...(activeTabKey && {
        status:
          activeTabKey === "1"
            ? "sent"
            : activeTabKey === "2"
            ? "scheduled"
            : "draft",
      }),
    }),
    [searchValue, filter, pageItems, currentPage, audience, activeTabKey]
  );

  // -------------------------------------------
  // 🔹 Base Messages (depends on active tab)
  // -------------------------------------------
  const {
    data: messages,
    error,
    isLoading,
    mutate,
  } = useSWR<PaginatedApplicationMessage>(
    token
      ? [
          "api/admin/app-messages",
          token,
          {
            ...query,
            status:
              activeTabKey === "1"
                ? "sent"
                : activeTabKey === "2"
                ? "scheduled"
                : "draft",
          },
        ]
      : null,
    swrFetcher
  );

  // -------------------------------------------
  // 🔹 Badge counts (independent queries)
  // -------------------------------------------
  const { data: scheduledMessages } = useSWR<PaginatedApplicationMessage>(
    token
      ? [
          "api/admin/app-messages",
          token,
          { ...query, status: "scheduled" },
        ]
      : null,
    swrFetcher
  );

  const { data: draftMessages } = useSWR<PaginatedApplicationMessage>(
    token
      ? ["api/admin/app-messages", token, { ...query, status: "draft" }]
      : null,
    swrFetcher
  );

  // -------------------------------------------
  // 🔹 Handlers
  // -------------------------------------------
  const toggleEditorModal = () => setOpenEditor((p) => !p);
  const toggleMainModal = () => setOpenModal((p) => !p);
  const toggleDrawer = () => setOpenGroup((p) => !p);

  const handleEdit = (_html: string, json: any) => {
    setContentJSONDraft(json);
    setContentHTMLDraft(_html);
  };

  const handleSave = async () => {
    if (!msgTitle)
      return generateResult("error", "Enter a message title before sending");
    if (!contentJSONDraft)
      return generateResult("error", "Create a message before sending");

    try {
      const fetcherArg: SwrMutationFetcherArgType = {
        token,
        requestOption: {
          method: "POST",
          body: JSON.stringify({
            title: msgTitle,
            body: contentHTMLDraft,
            audience: userType,
            meta: contentJSONDraft,
            sentAt: new Date().toISOString(),
          }),
          headers: {
            "Content-Type": "application/json",
          },
        },
      };
      const result: SuccessDataType<any> = await submitMessage(fetcherArg);
      generateResult("success", result.message);
      toggleEditorModal();
      mutate();
    } catch (error) {
      const e = parseClientError(error);
      generateResult("error", e.message || "something went wrong");
    }
  };
  const handleDraft = async () => {
    if (!msgTitle)
      return generateResult("error", "Enter a message title before sending");
    if (!contentJSONDraft)
      return generateResult("error", "Create a message before sending");

    try {
      const fetcherArg: SwrMutationFetcherArgType = {
        token,
        requestOption: {
          method: "POST",
          body: JSON.stringify({
            title: msgTitle,
            body: contentHTMLDraft,
            audience: userType,
            meta: contentJSONDraft,
            sentAt: null,
          }),
          headers: {
            "Content-Type": "application/json",
          },
        },
      };
      const result: SuccessDataType<any> = await submitDraftMessage(fetcherArg);
      generateResult("success", result.message);
      toggleEditorModal();
      mutate();
    } catch (error) {
      const e = parseClientError(error);
      generateResult("error", e.message || "something went wrong");
    }
  };
  const handleTabChange = (key: string) => {
    setActiveTabKey(key);
    setAudience("");
    setSearchValue("");
    setFilter("");
  };

  // -------------------------------------------
  // 🔹 Tabs definition
  // -------------------------------------------
  const tabs = [
    {
      key: "1",
      label: <h2>Sent</h2>,
      children: (
        <Sent
          key={activeTabKey}
          error={error}
          handleAudience={setAudience}
          handleCurrentPage={setCurrentPage}
          handleFilter={setFilter}
          handlePageItems={setPageItems}
          handleSearch={setSearchValue}
          isLoading={isLoading}
          mutate={mutate}
          pageItems={pageItems}
          sentMessages={messages!}
          msgType={
            activeTabKey === "1"
              ? "sent"
              : activeTabKey === "2"
              ? "scheduled"
              : "draft"
          }
        />
      ),
    },
    {
      key: "2",
      label: (
        <Badge
          offset={[9, 12]}
          count={scheduledMessages?.pagination.totalItems}
          color={activeTabKey === "2" ? "#d52b1e" : "#F0F2F5"}
          className={clsx({ [s.activeBadge]: activeTabKey === "2" })}
        >
          <h2>Scheduled</h2>
        </Badge>
      ),
      children: (
        <Sent
          key={activeTabKey}
          error={error}
          handleAudience={setAudience}
          handleCurrentPage={setCurrentPage}
          handleFilter={setFilter}
          handlePageItems={setPageItems}
          handleSearch={setSearchValue}
          isLoading={isLoading}
          mutate={mutate}
          pageItems={pageItems}
          sentMessages={messages!}
          msgType={
            activeTabKey === "1"
              ? "sent"
              : activeTabKey === "2"
              ? "scheduled"
              : "draft"
          }
        />
      ),
    },
    {
      key: "3",
      label: (
        <Badge
          offset={[9, 12]}
          count={draftMessages?.pagination.totalItems}
          color={activeTabKey === "3" ? "#d52b1e" : "#F0F2F5"}
          className={clsx({ [s.activeBadge]: activeTabKey === "3" })}
        >
          <h2>Draft</h2>
        </Badge>
      ),
      children: (
        <Sent
          key={activeTabKey}
          error={error}
          handleAudience={setAudience}
          handleCurrentPage={setCurrentPage}
          handleFilter={setFilter}
          handlePageItems={setPageItems}
          handleSearch={setSearchValue}
          isLoading={isLoading}
          mutate={mutate}
          pageItems={pageItems}
          sentMessages={messages!}
          msgType={
            activeTabKey === "1"
              ? "sent"
              : activeTabKey === "2"
              ? "scheduled"
              : "draft"
          }
        />
      ),
    },
  ];

  return (
    <>
      <div className={s.wrapper}>
        <div className={s.header}>
          <div className={s.titles}>
            <h3 className={s.title}>Send message</h3>
          </div>

          <div className={s.btnGroups}>
            <button className={s.scheduleBtn} onClick={toggleDrawer}>
              Schedule for later
            </button>
            <button className={s.sendBtn} onClick={toggleMainModal}>
              Create message
            </button>
          </div>
        </div>

        <TabsTemplate
          tabItems={tabs}
          wrapperClassname={s.customClass}
          onTabChange={handleTabChange}
          withBorderBottom
        />
      </div>

      {/* ---------------------
          Create Message Modal
      ---------------------- */}
      <ReusableModal
        title=""
        isModalOpen={openModal}
        handleModal={toggleMainModal}
      >
        <div className={s.cardWrapper}>
          {["all", "verified", "unverified", "pending"].map((type) => (
            <Card
              key={type}
              userType={
                type === "all"
                  ? "All users"
                  : type === "verified"
                  ? "Verified users"
                  : type === "unverified"
                  ? "Unverified users"
                  : "Pending users"
              }
              onClick={() => {
                setUserType(type as any);
                toggleEditorModal();
              }}
            />
          ))}
        </div>
      </ReusableModal>

      {/* ---------------------
          Editor Modal
      ---------------------- */}
      <ReusableModal
        isModalOpen={openEditor}
        handleModal={toggleEditorModal}
        footer={null}
        centered
        width={"90vw"}
        className={s.editorModal}
        closable={false}
        maskClosable={false}
        contentClassName={s.editorModal}
        title={
          <div className={s.header}>
            <Input
              value={msgTitle}
              count={{ show: true, max: 80 }}
              className={clsx(s.input, { [s.inputError]: !msgTitle })}
              maxLength={80}
              placeholder="This is the title or subject of email notifications."
              onChange={(e) => setMsgTitle(e.target.value)}
            />

            <div className={s.btnGroup}>
              <button onClick={toggleEditorModal} className={s.closeBtn}>
                <CloseIcon />
                Cancel
              </button>
              <Button
                loading={isMutating}
                className={s.saveBtn}
                onClick={handleSave}
              >
                <BaselineCheckCircle />
                Send message
              </Button>
              <Button
                className={s.saveBtn}
                loading={isSubmitting}
                onClick={handleDraft}
              >
                <BaselineCheckCircle />
                Save as draft
              </Button>
            </div>
          </div>
        }
        classNames={{
          body: s.modalBody,
          wrapper: s.modalWrapper,
          content: s.modalContent,
          footer: s.modalFooter,
          header: s.modalHeader,
          mask: s.modalMask,
        }}
      >
        <ReactEmailEditor
          handleEditorHTMLValue={handleEdit}
          jsonFormat={contentJSONDraft}
        />
      </ReusableModal>

      {/* ---------------------
         Schedule Drawer
      ---------------------- */}
      <ReusableDrawer
        title="Schedule message"
        Open={openGroup}
        handleOpen={toggleDrawer}
        classNames={{ body: s.drawerScheduleBody }}
      >
        <SetSchedule handleNewGroup={toggleDrawer} mutate={mutate} />
      </ReusableDrawer>
    </>
  );
}

export default SendMessageWrapper;
